import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';
import { useSettings } from '@wu/widget-core';
import type { Job } from '../../types';

type ShowPopupHandler = (jobId: Job['id']) => (() => void) | undefined;

export type PopupContextType = {
  isShowPopup: boolean;
  jobId: Job['id'];
  showPopup: () => void;
  hidePopup: () => void;
  handleShowPopup: ShowPopupHandler;
};

export const PopupContext = createContext<PopupContextType | null>(null);

export function usePopup() {
  const context = useContext<PopupContextType | null>(PopupContext);

  if (context === null) {
    throw new Error(`usePopup should be used within PopupProvider`);
  }
  return context;
}
export function PopupProvider({ children }: PropsWithChildren) {
  const [isShowPopup, setIsShowPopup] = useState(false);
  const [jobId, setJobId] = useState('');
  const { popupEnable } = useSettings();

  useEffect(() => {
    if (!popupEnable) {
      setIsShowPopup(false);
    }
  }, [popupEnable]);

  const handleShowPopup: ShowPopupHandler = useCallback(
    (jobId) => {
      if (!popupEnable) {
        return;
      }

      return () => {
        setIsShowPopup(true);
        setJobId(jobId);
      };
    },
    [popupEnable]
  );

  return (
    <PopupContext.Provider
      value={{
        isShowPopup,
        jobId,
        showPopup: () => setIsShowPopup(true),
        hidePopup: () => setIsShowPopup(false),
        handleShowPopup
      }}
    >
      {children}
    </PopupContext.Provider>
  );
}
